import React, { useState } from 'react'
import { Pressable, Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import type { InfoItem, ProductContentSubGroup } from '@flows/index'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import { body2, h4 } from '@ui-kit/core/typography'

import { CmsIcon } from './CmsIcon'
import { CoverageItemDescription } from './CoverageItemDescription'

const subGroupAccordionStyles = tv({
  slots: {
    itemRow: 'flex-row items-start gap-x-3 py-3',
    itemTextWrapper: 'flex-1',
    itemDescription: 'mt-2',
    headerRow: 'flex-row items-center gap-x-3 py-4',
    headerTitle: ['flex-1', h4()],
    itemsList: 'pl-9',
  },
  variants: {
    flat: {
      true: { itemRow: 'py-4', itemsList: 'pl-0' },
    },
  },
})

const CoverageInfoItem = ({
  item,
  isExpanded,
  onToggle,
  flat = false,
}: {
  item: InfoItem
  isExpanded: boolean
  onToggle: () => void
  flat?: boolean
}) => {
  const styles = subGroupAccordionStyles({ flat })

  return (
    <Pressable className={styles.itemRow()} onPress={onToggle}>
      <CmsIcon name={item.icon?.name} />
      <View className={styles.itemTextWrapper()}>
        <Text className={h4({ color: flat ? 'black' : 'deepForest' })}>{item.heading}</Text>
        {item.subheading ? (
          <Text className={body2({ color: 'grey500' })}>{item.subheading}</Text>
        ) : null}
        {isExpanded && item.description ? (
          <CoverageItemDescription
            className={styles.itemDescription()}
            description={item.description}
          />
        ) : null}
      </View>
      {item.description ? (
        <PhosphorIcon name={isExpanded ? 'CaretDown' : 'CaretRight'} size={24} />
      ) : null}
    </Pressable>
  )
}

const resolveSubGroupIconName = (subGroup: ProductContentSubGroup): string | undefined => {
  const iconRef = subGroup.icon
  if (!iconRef) {
    return undefined
  }
  const sizes = ['small', 'medium', 'large'] as const
  for (const size of sizes) {
    const name = iconRef[size]?.name
    if (name) {
      return name
    }
  }
  return undefined
}

export const SubGroupAccordion = ({
  subGroup,
  expandedItem,
  onToggleItem,
  defaultExpanded = false,
  flat = false,
}: {
  subGroup: ProductContentSubGroup
  expandedItem: string | null
  onToggleItem: (key: string) => void
  defaultExpanded?: boolean
  flat?: boolean
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const subGroupKey = subGroup.itemKey?.current ?? subGroup.title
  const iconName = resolveSubGroupIconName(subGroup)
  const styles = subGroupAccordionStyles({ flat })

  return (
    <View>
      {!flat && (
        <Pressable className={styles.headerRow()} onPress={() => setIsExpanded(!isExpanded)}>
          <CmsIcon name={iconName} />
          <Text className={styles.headerTitle()}>{subGroup.title}</Text>
          <PhosphorIcon name={isExpanded ? 'CaretDown' : 'CaretRight'} size={24} />
        </Pressable>
      )}
      {(flat || isExpanded) && (
        <View className={styles.itemsList()}>
          {subGroup.items?.map((item) => {
            const itemKey = item.itemKey ?? item.heading
            return (
              <CoverageInfoItem
                key={itemKey}
                item={item}
                flat={flat}
                isExpanded={expandedItem === `${subGroupKey}_${itemKey}`}
                onToggle={() =>
                  onToggleItem(
                    expandedItem === `${subGroupKey}_${itemKey}` ? '' : `${subGroupKey}_${itemKey}`
                  )
                }
              />
            )
          })}
        </View>
      )}
    </View>
  )
}
